<template>
  <b-container v-if="this.user.loggedIn">
    <b-alert v-if="generalUploadFeedback.text" :variant="generalUploadFeedback.variant" show class="mt-3">
      {{ generalUploadFeedback.text }}
    </b-alert>

    <div class="my-4" v-for="(config, index) in uploadConfigs" :key="config.key">
      <b-card class="upload-card" no-body>
        <div class="upload-card__header p-3">
          <div class="d-flex justify-content-between align-items-start flex-wrap">
            <div class="pr-3">
              <h4 class="mb-1">
                {{ config.title }}
                <small v-if="config.subtitle">({{ config.subtitle }})</small>
              </h4>
              <div class="text-muted small">
                Format: {{ config.acceptedFileTypes }}
                <template v-if="config.delimiterLabel">
                  <span class="mx-2">|</span>
                  Delimiter: {{ config.delimiterLabel }}
                </template>
                <span class="mx-2">|</span>
                {{ getExpectedColumnCount(config.key) }} Pflichtspalten
              </div>
            </div>

            <div class="d-flex flex-wrap upload-card__actions">
              <b-button
                variant="outline-secondary"
                size="sm"
                class="mb-2"
                @click="togglePanel(config.key, 'settings')">
                {{ isPanelOpen(config.key, 'settings') ? 'Spalteneinstellungen ausblenden' : 'Spalteneinstellungen' }}
              </b-button>
            </div>
          </div>

        </div>

        <div class="p-3 pt-0">
          <b-alert
            v-if="getUploadFeedback(config.key) && getUploadFeedback(config.key).text"
            :variant="getUploadFeedback(config.key).variant"
            show>
            {{ getUploadFeedback(config.key).text }}
          </b-alert>

          <FileUploadComponent
            :reduceTo="getEffectiveMapping(config.key)"
            :acceptedFileTypes="config.acceptedFileTypes"
            :fileType="config.fileType"
            :fileFormat="getEffectiveFileFormat(config.key)"
            @file-parsed="onFileParsed"
            @file-error="onFileError(config.key, $event)"></FileUploadComponent>

          <div class="upload-section-box mt-3">
            <div><strong>Erwartete Spaltenüberschriften</strong></div>
            <div class="upload-header-chips mt-2">
              <span
                class="upload-header-chip"
                v-for="header in getExpectedHeaderList(config.key)"
                :key="config.key + '-' + header">
                {{ header }}
              </span>
            </div>
            <small class="d-block text-muted mt-2">{{ config.infoText }}</small>
          </div>

          <b-collapse :visible="isPanelOpen(config.key, 'settings')" class="mt-3">
            <div class="upload-section-box">
              <div class="d-flex justify-content-between align-items-center flex-wrap">
                <strong>Erwartete Spaltenzuordnung (Einstellung)</strong>
                <small class="text-muted">Gilt global für diesen Upload-Typ.</small>
              </div>

              <div class="mt-3">
                <b-row
                  v-for="fieldKey in getConfigFields(config.key)"
                  :key="config.key + '-' + fieldKey"
                  class="mb-2 align-items-center">
                  <b-col md="4">
                    <small class="text-dark">{{ fieldKey }}</small>
                  </b-col>
                  <b-col md="8">
                    <b-form-input
                      :value="getEditableHeaderValue(config.key, fieldKey)"
                      :disabled="isSettingsSaving(config.key)"
                      @input="updateEditableHeader(config.key, fieldKey, $event)"
                      placeholder="Spaltenüberschrift"></b-form-input>
                  </b-col>
                </b-row>
              </div>

              <div class="mt-4">
                <strong>Dateiaufbau</strong>
                <b-row class="mt-2 align-items-center">
                  <b-col md="4">
                    <small class="text-dark">Zeilen am Anfang überspringen</small>
                  </b-col>
                  <b-col md="8">
                    <b-form-input
                      :value="getEditableFileFormatValue(config.key, 'skipLines')"
                      :disabled="isSettingsSaving(config.key)"
                      min="0"
                      type="number"
                      @input="updateEditableFileFormat(config.key, 'skipLines', $event)"></b-form-input>
                  </b-col>
                </b-row>

                <b-row v-if="isTextUpload(config)" class="mt-2 align-items-center">
                  <b-col md="4">
                    <small class="text-dark">Trennzeichen</small>
                  </b-col>
                  <b-col md="8">
                    <b-form-input
                      :value="getEditableFileFormatValue(config.key, 'delimiter')"
                      :disabled="isSettingsSaving(config.key)"
                      @input="updateEditableFileFormat(config.key, 'delimiter', $event)"></b-form-input>
                  </b-col>
                </b-row>

                <b-row v-if="isExcelUpload(config)" class="mt-2 align-items-center">
                  <b-col md="4">
                    <small class="text-dark">Tabellenblatt</small>
                  </b-col>
                  <b-col md="8">
                    <b-form-input
                      :value="getEditableFileFormatValue(config.key, 'sheetName')"
                      :disabled="isSettingsSaving(config.key)"
                      placeholder="Erstes Tabellenblatt"
                      @input="updateEditableFileFormat(config.key, 'sheetName', $event)"></b-form-input>
                    <small class="text-muted">Leer lässt das erste Tabellenblatt ausgewählt.</small>
                  </b-col>
                </b-row>
              </div>

              <div class="d-flex flex-wrap align-items-center mt-2">
                <b-button
                  variant="primary"
                  size="sm"
                  class="mr-2 mb-2"
                  :disabled="isSettingsSaving(config.key)"
                  @click="saveEditableMapping(config.key)">
                  <b-spinner
                    v-if="isSettingsSaving(config.key)"
                    small
                    label="Speichern"
                    variant="light"
                    class="mr-2"></b-spinner>
                  Speichern
                </b-button>

                <b-button
                  variant="outline-secondary"
                  size="sm"
                  class="mr-2 mb-2"
                  :disabled="isSettingsSaving(config.key)"
                  @click="resetEditableMappingToDefault(config.key)">
                  Auf Default zurücksetzen
                </b-button>

                <small
                  v-if="getSettingsMessage(config.key)"
                  class="upload-settings__message mb-2"
                  :class="settingsMessageClass(config.key)">
                  {{ getSettingsMessage(config.key).text }}
                </small>
              </div>
            </div>
          </b-collapse>
        </div>
      </b-card>

      <hr v-if="index < uploadConfigs.length - 1" />
    </div>

    <b-modal
      id="upload-header-mapping-dialog"
      v-model="headerDialog.show"
      title="Spaltenzuordnung anpassen"
      hide-footer
      no-close-on-backdrop
      @hidden="resetHeaderDialogState">
      <p class="mb-2">
        Die Dateiüberschriften weichen von den erwarteten Einstellungen ab.
        Bitte ordnen Sie die Spalten neu zu.
      </p>

      <div v-if="headerDialog.missingFields.length > 0" class="mb-3">
        <small class="text-muted d-block mb-1">Nicht automatisch gefunden:</small>
        <ul class="mb-0">
          <li v-for="fieldKey in headerDialog.missingFields" :key="'missing-' + fieldKey">
            {{ fieldKey }} (erwartet: {{ getExpectedHeaderForField(headerDialog.uploadKey, fieldKey) }})
          </li>
        </ul>
      </div>

      <b-row
        v-for="fieldKey in getDialogFields()"
        :key="'dialog-' + fieldKey"
        class="mb-3 align-items-center">
        <b-col md="5">
          <strong>{{ fieldKey }}</strong>
          <div>
            <small class="text-muted">
              Erwartet: {{ getExpectedHeaderForField(headerDialog.uploadKey, fieldKey) }}
            </small>
          </div>
        </b-col>
        <b-col md="7">
          <b-form-select
            :value="getDialogMappingValue(fieldKey)"
            :options="getDialogHeaderOptions()"
            @change="updateDialogMapping(fieldKey, $event)"></b-form-select>
        </b-col>
      </b-row>

      <b-form-checkbox v-model="headerDialog.saveAsDefault" class="mb-2">
        Zuordnung als Standard für diesen Upload-Typ speichern
      </b-form-checkbox>

      <small v-if="headerDialog.errorMessage" class="text-danger d-block mb-2">
        {{ headerDialog.errorMessage }}
      </small>

      <div class="d-flex justify-content-end mt-3">
        <b-button
          variant="secondary"
          class="mr-2"
          :disabled="headerDialog.processing"
          @click="headerDialog.show = false">
          Abbrechen
        </b-button>
        <b-button
          variant="primary"
          :disabled="headerDialog.processing"
          @click="applyHeaderDialogMapping">
          <b-spinner
            v-if="headerDialog.processing"
            small
            label="Übernehme"
            variant="light"
            class="mr-2"></b-spinner>
          Übernehmen
        </b-button>
      </div>
    </b-modal>
  </b-container>
</template>

<script>
import {mapGetters} from "vuex";
import FileUploadComponent from "./FileUploadComponent.vue";
import {auth, db, functions, firestore} from "../plugins/firebase";
import {set, ref, onValue} from "firebase/database";
import {doc, onSnapshot} from "firebase/firestore";
import {httpsCallable} from "firebase/functions";
import {storage} from "../plugins/firebase";
import {ref as storageRef, uploadBytes} from "firebase/storage";

const UPLOAD_CONFIGS = [
  {
    key: "appleFinishedGoodsReport",
    title: "Apple FG-Report",
    fileType: "Apple Finished Goods Report",
    acceptedFileTypes: ".txt",
    delimiter: "\t",
    delimiterLabel: "Tabulator",
    skipLines: 5,
    infoText:
      "Zeilen vor der ersten Zeile mit 'Shipping Status' werden übersprungen. Weitere Spalten werden ignoriert.",
    defaultMapping: {
      shipStatus: "Shipping Status",
      customerPONumber: "Customer PO Number",
      applePart: "Apple Part #",
      DNQuantity: "DN Quantity",
      DNCreationDate: "DN Creation Date",
      estimatedDeliveryDateonorbefore: "Estimated Delivery Date on or before",
      DNValue: "DN Value",
    },
  },
  {
    key: "appleSerialNumbers",
    title: "Apple IMEI-Report",
    fileType: "Apple Seriennummern",
    acceptedFileTypes: ".txt, .csv, .tsv",
    delimiter: "\t",
    delimiterLabel: "Tabulator",
    skipLines: 5,
    infoText:
      "Zeilen vor der ersten Zeile mit 'Apple Part Number' werden übersprungen. Weitere Spalten werden ignoriert.",
    defaultMapping: {
      applePartNumber: "Apple Part Number",
      serialNumber: "Serial Number",
      macAddress: "MAC Address",
      imei: "IMEI Number",
      eid: "CSN/EID",
    },
  },
  /* {
    key: "telekomAvis",
    title: "Telekom Avis",
    fileType: "Telekom iPhone Avisierungen",
    acceptedFileTypes: ".xls, .xlsx",
    skipLines: 1,
    infoText:
      "Zeilen vor der ersten Zeile mit 'Kundenreferenz' werden übersprungen. Weitere Spalten werden ignoriert.",
    defaultMapping: {
      orderNo: "Kundenreferenz",
      supplierArtNo: "Artikelnummer",
      amount: "Liefermenge",
      shippingDate: "Quittierungsdatum",
    },
  }, */
  {
    key: "telekomImei",
    title: "Telekom IMEI",
    fileType: "Telekom IMEI",
    acceptedFileTypes: ".xlsx",
    skipLines: 1,
    infoText:
      "Zeilen vor der ersten Zeile mit 'Artikelnummer' werden übersprungen. Weitere Spalten werden ignoriert.",
    defaultMapping: {
      supplierArtNo: "Artikelnummer",
      serialNumber: "Geräte SNR",
      imei: "SNR2 (IMEI)",
      eid: "SNR3 (eID)",
    },
  },
  {
    key: "telekomSupplierArtNos",
    title: "Telekom Artikelnummern",
    subtitle: "zum NPI erneuern",
    fileType: "iPhone Telekom Ersatzlieferanten Artikelnummern",
    acceptedFileTypes: ".txt",
    delimiter: "\t",
    delimiterLabel: "Tabulator",
    skipLines: 0,
    infoText:
      "Zeilen vor der ersten Zeile mit 'BestNr // ErsatzLief' werden übersprungen. Weitere Spalten werden ignoriert. Die Datei muss nach jedem iPhone NPI erneut importiert werden.",
    defaultMapping: {
      manufacturerArtNo: "Hersteller_Artikelnummer",
      supplierArtNo: "BestNr // ErsatzLief",
    },
  },
];

export default {
  name: "DataImport",
  computed: {
    ...mapGetters({
      user: "user",
    }),
  },
  components: {
    FileUploadComponent,
  },
  data() {
    return {
      isMigrating: false,
      migrationVariant: "secondary",
      migrationMessage: "",
      migrationMetaUnsubscribe: null,
      uploadConfigs: UPLOAD_CONFIGS,
      savedUploadColumnConfig: {},
      effectiveUploadMappings: {},
      editableUploadMappings: {},
      effectiveUploadFileFormats: {},
      editableUploadFileFormats: {},
      settingsMessages: {},
      settingsSaving: {},
      panelStateByUploadKey: {},
      uploadFeedbackByKey: {},
      generalUploadFeedback: {
        variant: "info",
        text: "",
      },
      activeUploadStatusUnsubscribe: null,
      activeUploadStatusId: null,
      activeUploadStatusTimeoutId: null,
      headerDialog: {
        show: false,
        uploadKey: null,
        fileType: null,
        parsedRows: [],
        detectedHeaders: [],
        mappingDraft: {},
        missingFields: [],
        saveAsDefault: false,
        errorMessage: "",
        processing: false,
      },
    };
  },
  methods: {
    initPanelStates() {
      this.uploadConfigs.forEach((config) => {
        if (!this.panelStateByUploadKey[config.key]) {
          this.$set(this.panelStateByUploadKey, config.key, {
            settings: false,
          });
        }
      });
    },
    togglePanel(uploadKey, panelName) {
      if (!this.panelStateByUploadKey[uploadKey]) {
        this.$set(this.panelStateByUploadKey, uploadKey, {
          settings: false,
        });
      }
      const panelState = this.panelStateByUploadKey[uploadKey];
      this.$set(panelState, panelName, !panelState[panelName]);
    },
    isPanelOpen(uploadKey, panelName) {
      const panelState = this.panelStateByUploadKey[uploadKey];
      if (!panelState) {
        return false;
      }
      return panelState[panelName] === true;
    },
    getExpectedColumnCount(uploadKey) {
      return this.getExpectedHeaderList(uploadKey).length;
    },
    setUploadFeedback(uploadKey, variant, text) {
      const feedback = {variant, text};
      if (uploadKey) {
        this.$set(this.uploadFeedbackByKey, uploadKey, feedback);
      } else {
        this.generalUploadFeedback = feedback;
      }
    },
    getUploadFeedback(uploadKey) {
      return this.uploadFeedbackByKey[uploadKey] || null;
    },
    generateUploadId(fileType) {
      const normalizedFileType = (fileType || "upload")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 32);
      const randomSuffix = Math.random().toString(36).slice(2, 8);
      return `${normalizedFileType || "upload"}-${Date.now()}-${randomSuffix}`;
    },
    formatUploadProcessingError(errorMessage) {
      if (typeof errorMessage !== "string" || errorMessage.trim() === "") {
        return "Unbekannter Fehler in der Serververarbeitung.";
      }

      return errorMessage.replace(/\s+/g, " ").trim().slice(0, 220);
    },
    clearUploadStatusListener() {
      if (typeof this.activeUploadStatusUnsubscribe === "function") {
        this.activeUploadStatusUnsubscribe();
      }
      this.activeUploadStatusUnsubscribe = null;
      this.activeUploadStatusId = null;

      if (this.activeUploadStatusTimeoutId) {
        clearTimeout(this.activeUploadStatusTimeoutId);
        this.activeUploadStatusTimeoutId = null;
      }
    },
    startUploadStatusListener({uploadKey, uploadId, fileType, storagePath}) {
      this.clearUploadStatusListener();
      this.activeUploadStatusId = uploadId;

      this.setUploadFeedback(
        uploadKey,
        "info",
        `Datei für '${fileType}' hochgeladen. Verarbeitung wird gestartet...`
      );

      const uploadStatusRef = ref(db, `uploadProcessing/${uploadId}`);
      this.activeUploadStatusUnsubscribe = onValue(
        uploadStatusRef,
        (snapshot) => {
          if (!snapshot.exists()) {
            return;
          }

          const statusData = snapshot.val() || {};
          const status = typeof statusData.status === "string" ? statusData.status : "";

          if (status === "processing" || status === "uploaded") {
            this.setUploadFeedback(
              uploadKey,
              "info",
              `Datei für '${fileType}' hochgeladen. Verarbeitung läuft...`
            );
            return;
          }

          if (status === "success") {
            this.setUploadFeedback(
              uploadKey,
              "success",
              `Verarbeitung für '${fileType}' erfolgreich abgeschlossen.`
            );
            this.clearUploadStatusListener();
            return;
          }

          if (status === "failed") {
            const readableError = this.formatUploadProcessingError(statusData.errorMessage);
            this.setUploadFeedback(
              uploadKey,
              "danger",
              `Verarbeitung für '${fileType}' fehlgeschlagen: ${readableError}`
            );
            this.clearUploadStatusListener();
          }
        },
        (error) => {
          console.error("Fehler beim Beobachten des Upload-Status:", {
            error,
            uploadId,
            fileType,
            storagePath,
          });
          this.setUploadFeedback(
            uploadKey,
            "warning",
            `Datei für '${fileType}' hochgeladen. Der Verarbeitungsstatus konnte nicht live geladen werden.`
          );
          this.clearUploadStatusListener();
        }
      );

      this.activeUploadStatusTimeoutId = setTimeout(() => {
        if (this.activeUploadStatusId !== uploadId) {
          return;
        }

        this.setUploadFeedback(
          "warning",
          `Datei für '${fileType}' hochgeladen. Die Verarbeitung dauert länger als erwartet. Bitte später erneut prüfen.`
        );
      }, 45000);
    },
    buildStorageUploadMetadata(fileType, uploadId) {
      return {
        contentType: "application/json",
        customMetadata: {
          uploadId,
          fileType: fileType || "",
          uploadedBy: auth.currentUser && auth.currentUser.uid ? auth.currentUser.uid : "unknown",
          uploadedAt: String(Date.now()),
        },
      };
    },
    async uploadJsonWithProcessingTracking(uploadKey, fileType, storagePath, rows) {
      const uploadId = this.generateUploadId(fileType);
      const jsonBlob = new Blob([JSON.stringify(rows, null, 2)], {
        type: "application/json",
      });

      const storageReference = storageRef(storage, storagePath);
      await uploadBytes(
        storageReference,
        jsonBlob,
        this.buildStorageUploadMetadata(fileType, uploadId)
      );

      this.startUploadStatusListener({
        uploadKey,
        uploadId,
        fileType,
        storagePath,
      });

      return {
        processingDeferred: true,
        uploadId,
      };
    },
    settingsMessageClass(uploadKey) {
      const message = this.getSettingsMessage(uploadKey);
      if (!message) {
        return "";
      }
      return message.variant === "danger" ? "text-danger" : "text-success";
    },
    resolveTotalChunks(totalChunks, totalDocs, chunkSize) {
      const normalizedTotalChunks = Number(totalChunks || 0);
      const normalizedTotalDocs = Number(totalDocs || 0);
      const normalizedChunkSize = Number(chunkSize || 0);

      const calculatedTotalChunks =
        normalizedTotalDocs > 0 && normalizedChunkSize > 0
          ? Math.ceil(normalizedTotalDocs / normalizedChunkSize)
          : 0;

      if (normalizedTotalChunks > 0 && calculatedTotalChunks > 0) {
        return Math.max(normalizedTotalChunks, calculatedTotalChunks);
      }

      if (normalizedTotalChunks > 0) {
        return normalizedTotalChunks;
      }

      if (calculatedTotalChunks > 0) {
        return calculatedTotalChunks;
      }

      return 0;
    },
    startMigrationProgressListener() {
      if (this.migrationMetaUnsubscribe) {
        return;
      }

      const migrationMetaRef = doc(
        firestore,
        "device-info-migration-meta",
        "device-infos"
      );

      this.migrationMetaUnsubscribe = onSnapshot(
        migrationMetaRef,
        (snapshot) => {
          if (!snapshot.exists()) {
            return;
          }

          const meta = snapshot.data() || {};
          const processedChunks = Number(meta.processedChunks || 0);
          const totalChunks = Number(meta.totalChunks || 0);
          const totalDocs = Number(meta.totalDocs || 0);
          const chunkSize = Number(meta.chunkSize || 0);
          const safeTotalChunks = this.resolveTotalChunks(
            totalChunks,
            totalDocs,
            chunkSize
          );
          const totalChunksLabel = safeTotalChunks > 0 ? safeTotalChunks : "?";
          const status = meta.status || null;
          const isRunning =
            meta.running === true || status === "running" || status === "queued";
          const progressText = `Chunks: ${processedChunks} von ${totalChunksLabel}`;

          if (isRunning) {
            this.isMigrating = true;
            this.migrationVariant = "info";
            this.migrationMessage = progressText;
            return;
          }

          if (status === "completed") {
            this.isMigrating = false;
            this.migrationVariant = "success";
            this.migrationMessage = `Migration abgeschlossen. ${progressText}`;
            return;
          }

          if (status === "failed") {
            this.isMigrating = false;
            this.migrationVariant = "danger";
            this.migrationMessage = meta.lastError
              ? `Migration fehlgeschlagen: ${meta.lastError}. ${progressText}`
              : `Migration fehlgeschlagen. ${progressText}`;
          }
        },
        (error) => {
          console.error("Fehler beim Beobachten des Migrationsfortschritts:", error);
        }
      );
    },
    loadUploadColumnConfig() {
      onValue(ref(db, "/settings/uploadColumnConfig"), (snapshot) => {
        this.savedUploadColumnConfig = snapshot.val() || {};
        this.refreshUploadMappings();
      });
    },
    normalizeHeader(header) {
      if (typeof header !== "string") {
        return "";
      }
      return header.trim().toLowerCase();
    },
    getConfigByKey(uploadKey) {
      return this.uploadConfigs.find((config) => config.key === uploadKey);
    },
    getConfigByFileType(fileType) {
      return this.uploadConfigs.find((config) => config.fileType === fileType);
    },
    getConfigFields(uploadKey) {
      const config = this.getConfigByKey(uploadKey);
      if (!config) {
        return [];
      }
      return Object.keys(config.defaultMapping);
    },
    getEffectiveMapping(uploadKey) {
      if (this.effectiveUploadMappings[uploadKey]) {
        return this.effectiveUploadMappings[uploadKey];
      }

      const config = this.getConfigByKey(uploadKey);
      return config ? {...config.defaultMapping} : {};
    },
    getDefaultFileFormat(config) {
      return {
        skipLines: config ? config.skipLines || 0 : 0,
        delimiter: config && typeof config.delimiter === "string" ? config.delimiter : "",
        sheetName: "",
      };
    },
    getEffectiveFileFormat(uploadKey) {
      const fileFormat = this.effectiveUploadFileFormats[uploadKey];
      if (fileFormat) {
        return fileFormat;
      }

      return this.getDefaultFileFormat(this.getConfigByKey(uploadKey));
    },
    isExcelUpload(config) {
      return config && config.acceptedFileTypes.includes(".xlsx");
    },
    isTextUpload(config) {
      return config && !this.isExcelUpload(config);
    },
    getExpectedHeaderList(uploadKey) {
      const mapping = this.getEffectiveMapping(uploadKey);
      return Object.values(mapping);
    },
    getExpectedHeaderForField(uploadKey, fieldKey) {
      const mapping = this.getEffectiveMapping(uploadKey);
      return mapping[fieldKey] || "";
    },
    updateEditableHeader(uploadKey, fieldKey, value) {
      if (!this.editableUploadMappings[uploadKey]) {
        this.$set(this.editableUploadMappings, uploadKey, {});
      }
      this.$set(this.editableUploadMappings[uploadKey], fieldKey, value);
    },
    getEditableHeaderValue(uploadKey, fieldKey) {
      if (
        this.editableUploadMappings[uploadKey] &&
        typeof this.editableUploadMappings[uploadKey][fieldKey] === "string"
      ) {
        return this.editableUploadMappings[uploadKey][fieldKey];
      }

      const mapping = this.getEffectiveMapping(uploadKey);
      return mapping[fieldKey] || "";
    },
    updateEditableFileFormat(uploadKey, fieldKey, value) {
      if (!this.editableUploadFileFormats[uploadKey]) {
        this.$set(this.editableUploadFileFormats, uploadKey, {});
      }
      this.$set(this.editableUploadFileFormats[uploadKey], fieldKey, value);
    },
    getEditableFileFormatValue(uploadKey, fieldKey) {
      const fileFormat = this.editableUploadFileFormats[uploadKey];
      if (fileFormat && fileFormat[fieldKey] !== undefined) {
        return fileFormat[fieldKey];
      }

      return this.getEffectiveFileFormat(uploadKey)[fieldKey];
    },
    isSettingsSaving(uploadKey) {
      return this.settingsSaving[uploadKey] === true;
    },
    setSettingsSaving(uploadKey, isSaving) {
      this.$set(this.settingsSaving, uploadKey, isSaving);
    },
    setSettingsMessage(uploadKey, variant, text) {
      this.$set(this.settingsMessages, uploadKey, {
        variant,
        text,
      });
    },
    getSettingsMessage(uploadKey) {
      return this.settingsMessages[uploadKey] || null;
    },
    refreshUploadMappings() {
      this.uploadConfigs.forEach((config) => {
        const saved = this.savedUploadColumnConfig[config.key] || {};
        const savedMapping = saved.mapping || saved;
        const defaultFileFormat = this.getDefaultFileFormat(config);
        const savedFileFormat = saved.fileFormat || {};
        const merged = {};

        Object.keys(config.defaultMapping).forEach((fieldKey) => {
          const savedValue =
            typeof savedMapping[fieldKey] === "string" ? savedMapping[fieldKey].trim() : "";
          merged[fieldKey] = savedValue || config.defaultMapping[fieldKey];
        });

        const mergedFileFormat = {
          skipLines: Number.isInteger(Number(savedFileFormat.skipLines)) && Number(savedFileFormat.skipLines) >= 0
            ? Number(savedFileFormat.skipLines)
            : defaultFileFormat.skipLines,
          delimiter: typeof savedFileFormat.delimiter === "string"
            ? savedFileFormat.delimiter
            : defaultFileFormat.delimiter,
          sheetName: typeof savedFileFormat.sheetName === "string"
            ? savedFileFormat.sheetName
            : defaultFileFormat.sheetName,
        };

        this.$set(this.effectiveUploadMappings, config.key, merged);
        this.$set(this.effectiveUploadFileFormats, config.key, mergedFileFormat);

        if (!this.editableUploadMappings[config.key]) {
          this.$set(this.editableUploadMappings, config.key, {});
        }

        Object.keys(merged).forEach((fieldKey) => {
          this.$set(this.editableUploadMappings[config.key], fieldKey, merged[fieldKey]);
        });

        if (!this.editableUploadFileFormats[config.key]) {
          this.$set(this.editableUploadFileFormats, config.key, {});
        }
        Object.keys(mergedFileFormat).forEach((fieldKey) => {
          this.$set(this.editableUploadFileFormats[config.key], fieldKey, mergedFileFormat[fieldKey]);
        });
      });
    },
    getSanitizedMapping(uploadKey, mappingCandidate) {
      const config = this.getConfigByKey(uploadKey);
      if (!config) {
        return {};
      }

      const sanitized = {};
      Object.keys(config.defaultMapping).forEach((fieldKey) => {
        const value =
          mappingCandidate && typeof mappingCandidate[fieldKey] === "string"
            ? mappingCandidate[fieldKey].trim()
            : "";
        sanitized[fieldKey] = value;
      });

      return sanitized;
    },
    validateMapping(uploadKey, mappingCandidate) {
      const config = this.getConfigByKey(uploadKey);
      if (!config) {
        return {
          valid: false,
          message: "Upload-Typ konnte nicht ermittelt werden.",
        };
      }

      const fields = Object.keys(config.defaultMapping);
      const missing = fields.filter((fieldKey) => {
        const value = mappingCandidate[fieldKey];
        return typeof value !== "string" || value.trim() === "";
      });

      if (missing.length > 0) {
        return {
          valid: false,
          message: `Bitte ordnen Sie alle Felder zu. Fehlend: ${missing.join(", ")}`,
        };
      }

      const normalizedSelections = fields.map((fieldKey) =>
        this.normalizeHeader(mappingCandidate[fieldKey])
      );
      const unique = new Set(normalizedSelections);

      if (unique.size !== normalizedSelections.length) {
        return {
          valid: false,
          message: "Jede Spalte darf nur einmal zugeordnet werden.",
        };
      }

      return {
        valid: true,
        message: "",
      };
    },
    getSanitizedFileFormat(uploadKey, fileFormatCandidate) {
      const config = this.getConfigByKey(uploadKey);
      const defaults = this.getDefaultFileFormat(config);
      const skipLines = Number(fileFormatCandidate && fileFormatCandidate.skipLines);

      return {
        skipLines: Number.isInteger(skipLines) && skipLines >= 0 ? skipLines : defaults.skipLines,
        delimiter: fileFormatCandidate && typeof fileFormatCandidate.delimiter === "string"
          ? fileFormatCandidate.delimiter
          : defaults.delimiter,
        sheetName: fileFormatCandidate && typeof fileFormatCandidate.sheetName === "string"
          ? fileFormatCandidate.sheetName.trim()
          : defaults.sheetName,
      };
    },
    validateFileFormat(uploadKey, fileFormatCandidate) {
      const config = this.getConfigByKey(uploadKey);
      if (!config) {
        return {valid: false, message: "Upload-Typ konnte nicht ermittelt werden."};
      }

      if (!Number.isInteger(fileFormatCandidate.skipLines) || fileFormatCandidate.skipLines < 0) {
        return {valid: false, message: "Die Anzahl übersprungener Zeilen muss eine nichtnegative ganze Zahl sein."};
      }

      if (this.isTextUpload(config) && fileFormatCandidate.delimiter.length !== 1) {
        return {valid: false, message: "Das Trennzeichen muss genau ein Zeichen enthalten."};
      }

      return {valid: true, message: ""};
    },
    async persistUploadColumnConfig(uploadKey, mappingCandidate, fileFormatCandidate) {
      const sanitized = this.getSanitizedMapping(uploadKey, mappingCandidate);
      const validation = this.validateMapping(uploadKey, sanitized);
      if (!validation.valid) {
        throw new Error(validation.message);
      }

      const sanitizedFileFormat = this.getSanitizedFileFormat(uploadKey, fileFormatCandidate);
      const fileFormatValidation = this.validateFileFormat(uploadKey, sanitizedFileFormat);
      if (!fileFormatValidation.valid) {
        throw new Error(fileFormatValidation.message);
      }

      const template = {mapping: sanitized, fileFormat: sanitizedFileFormat};
      await set(ref(db, `settings/uploadColumnConfig/${uploadKey}`), template);
      this.$set(this.savedUploadColumnConfig, uploadKey, template);
      this.refreshUploadMappings();
    },
    async saveEditableMapping(uploadKey) {
      const mappingCandidate = this.getSanitizedMapping(
        uploadKey,
        this.editableUploadMappings[uploadKey]
      );
      const validation = this.validateMapping(uploadKey, mappingCandidate);
      if (!validation.valid) {
        this.setSettingsMessage(uploadKey, "danger", validation.message);
        return;
      }

      const fileFormatCandidate = this.getSanitizedFileFormat(
        uploadKey,
        this.editableUploadFileFormats[uploadKey]
      );
      const fileFormatValidation = this.validateFileFormat(uploadKey, fileFormatCandidate);
      if (!fileFormatValidation.valid) {
        this.setSettingsMessage(uploadKey, "danger", fileFormatValidation.message);
        return;
      }

      this.setSettingsSaving(uploadKey, true);
      try {
        await this.persistUploadColumnConfig(uploadKey, mappingCandidate, fileFormatCandidate);
        this.setSettingsMessage(uploadKey, "success", "Importvorlage gespeichert.");
      } catch (error) {
        console.error("Fehler beim Speichern der Spaltenzuordnung:", error);
        this.setSettingsMessage(
          uploadKey,
          "danger",
          error.message || "Importvorlage konnte nicht gespeichert werden."
        );
      } finally {
        this.setSettingsSaving(uploadKey, false);
      }
    },
    async resetEditableMappingToDefault(uploadKey) {
      const config = this.getConfigByKey(uploadKey);
      if (!config) {
        return;
      }

      Object.keys(config.defaultMapping).forEach((fieldKey) => {
        this.updateEditableHeader(uploadKey, fieldKey, config.defaultMapping[fieldKey]);
      });
      const defaultFileFormat = this.getDefaultFileFormat(config);
      Object.keys(defaultFileFormat).forEach((fieldKey) => {
        this.updateEditableFileFormat(uploadKey, fieldKey, defaultFileFormat[fieldKey]);
      });

      await this.saveEditableMapping(uploadKey);
    },
    createDetectedHeaderLookup(detectedHeaders) {
      const lookup = {};

      detectedHeaders.forEach((header) => {
        if (typeof header !== "string") {
          return;
        }
        const normalized = this.normalizeHeader(header);
        if (!normalized) {
          return;
        }
        if (!lookup[normalized]) {
          lookup[normalized] = header;
        }
      });

      return lookup;
    },
    resolveAutoMapping(uploadKey, detectedHeaders) {
      const mapping = this.getEffectiveMapping(uploadKey);
      const lookup = this.createDetectedHeaderLookup(detectedHeaders);
      const resolvedMapping = {};
      const missingFields = [];

      Object.keys(mapping).forEach((fieldKey) => {
        const expectedHeader = mapping[fieldKey];
        const normalizedExpected = this.normalizeHeader(expectedHeader);
        const matchingDetectedHeader = lookup[normalizedExpected];

        if (matchingDetectedHeader) {
          resolvedMapping[fieldKey] = matchingDetectedHeader;
        } else {
          resolvedMapping[fieldKey] = "";
          missingFields.push(fieldKey);
        }
      });

      return {
        resolvedMapping,
        missingFields,
      };
    },
    async onFileParsed(payload) {
      const parsedRows = payload && payload.parsedRows ? payload.parsedRows : [];
      const detectedHeaders = payload && payload.detectedHeaders ? payload.detectedHeaders : [];
      const fileType = payload && payload.fileType ? payload.fileType : null;

      if (!fileType) {
        return;
      }

      const config = this.getConfigByFileType(fileType);

      if (!Array.isArray(parsedRows) || parsedRows.length === 0) {
        this.setUploadFeedback(
          config ? config.key : null,
          "warning",
          "Die Datei enthält keine Datensätze."
        );
        return;
      }

      if (!config) {
        this.setUploadFeedback(null, "danger", "Unbekannter Upload-Typ.");
        return;
      }

      const effectiveDetectedHeaders =
        Array.isArray(detectedHeaders) && detectedHeaders.length > 0
          ? detectedHeaders
          : Object.keys(parsedRows[0] || {});

      const {resolvedMapping, missingFields} = this.resolveAutoMapping(
        config.key,
        effectiveDetectedHeaders
      );

      if (missingFields.length === 0) {
        await this.processParsedUpload(config.key, fileType, parsedRows, resolvedMapping);
        return;
      }

      this.setUploadFeedback(
        config.key,
        "warning",
        "Abweichende Spalten erkannt. Bitte Zuordnung im Dialog prüfen."
      );

      this.headerDialog.show = true;
      this.headerDialog.uploadKey = config.key;
      this.headerDialog.fileType = fileType;
      this.headerDialog.parsedRows = parsedRows;
      this.headerDialog.detectedHeaders = effectiveDetectedHeaders;
      this.headerDialog.mappingDraft = {...resolvedMapping};
      this.headerDialog.missingFields = missingFields;
      this.headerDialog.saveAsDefault = false;
      this.headerDialog.errorMessage = "";
      this.headerDialog.processing = false;
    },
    getDialogFields() {
      if (!this.headerDialog.uploadKey) {
        return [];
      }
      return this.getConfigFields(this.headerDialog.uploadKey);
    },
    getDialogHeaderOptions() {
      const options = [
        {
          value: "",
          text: "Bitte Spalte auswählen",
        },
      ];

      const headerSet = new Set();
      this.headerDialog.detectedHeaders.forEach((header) => {
        if (typeof header !== "string") {
          return;
        }
        const normalized = this.normalizeHeader(header);
        if (!normalized || headerSet.has(normalized)) {
          return;
        }
        headerSet.add(normalized);
        options.push({
          value: header,
          text: header,
        });
      });

      return options;
    },
    getDialogMappingValue(fieldKey) {
      if (!this.headerDialog.mappingDraft[fieldKey]) {
        return "";
      }
      return this.headerDialog.mappingDraft[fieldKey];
    },
    updateDialogMapping(fieldKey, selectedHeader) {
      this.$set(this.headerDialog.mappingDraft, fieldKey, selectedHeader);
    },
    resetHeaderDialogState() {
      this.headerDialog.uploadKey = null;
      this.headerDialog.fileType = null;
      this.headerDialog.parsedRows = [];
      this.headerDialog.detectedHeaders = [];
      this.headerDialog.mappingDraft = {};
      this.headerDialog.missingFields = [];
      this.headerDialog.saveAsDefault = false;
      this.headerDialog.errorMessage = "";
      this.headerDialog.processing = false;
    },
    async applyHeaderDialogMapping() {
      if (this.headerDialog.processing) {
        return;
      }

      const uploadKey = this.headerDialog.uploadKey;
      const fileType = this.headerDialog.fileType;
      const parsedRows = this.headerDialog.parsedRows;
      const mappingCandidate = this.getSanitizedMapping(
        uploadKey,
        this.headerDialog.mappingDraft
      );

      const validation = this.validateMapping(uploadKey, mappingCandidate);
      if (!validation.valid) {
        this.headerDialog.errorMessage = validation.message;
        return;
      }

      this.headerDialog.errorMessage = "";
      this.headerDialog.processing = true;

      try {
        await this.processParsedUpload(uploadKey, fileType, parsedRows, mappingCandidate);

        if (this.headerDialog.saveAsDefault) {
          try {
            await this.persistUploadColumnConfig(
              uploadKey,
              mappingCandidate,
              this.getEffectiveFileFormat(uploadKey)
            );
            this.setSettingsMessage(uploadKey, "success", "Importvorlage gespeichert.");
          } catch (error) {
            console.error("Speichern der Standardzuordnung fehlgeschlagen:", error);
            this.setSettingsMessage(
              uploadKey,
              "danger",
              error.message || "Zuordnung konnte nicht als Standard gespeichert werden."
            );
          }
        }

        this.headerDialog.show = false;
      } catch (error) {
        console.error("Fehler beim Übernehmen der Spaltenzuordnung:", error);
        this.headerDialog.errorMessage =
          error.message || "Upload konnte mit dieser Zuordnung nicht abgeschlossen werden.";
      } finally {
        this.headerDialog.processing = false;
      }
    },
    reduceParsedData(rows, mapping) {
      return rows.map((row) => {
        const mappedRow = {};
        Object.keys(mapping).forEach((fieldKey) => {
          mappedRow[fieldKey] = row[mapping[fieldKey]];
        });
        return mappedRow;
      });
    },
    onFileError(uploadKey, errorMessage) {
      this.setUploadFeedback(uploadKey, "danger", errorMessage || "Datei konnte nicht eingelesen werden.");
    },
    chunkArray(array, chunkSize) {
      const chunked = [];
      for (let i = 0; i < array.length; i += chunkSize) {
        chunked.push(array.slice(i, i + chunkSize));
      }
      return chunked;
    },
    async processParsedUpload(uploadKey, fileType, parsedRows, mapping) {
      const reduced = this.reduceParsedData(parsedRows, mapping);
      const chunks = this.chunkArray(reduced, 5000);

      if (!Array.isArray(chunks) || chunks.length === 0) {
        this.setUploadFeedback(uploadKey, "warning", "Keine verarbeitbaren Daten gefunden.");
        return;
      }

      try {
        const persistResult = await this.persistUploadData({
          uploadKey,
          fileType,
          fileData: chunks,
        });

        if (persistResult && persistResult.processingDeferred === true) {
          return;
        }

        this.setUploadFeedback(uploadKey, "success", `Import für '${fileType}' abgeschlossen.`);
      } catch (error) {
        console.error("Fehler beim Verarbeiten des Uploads:", error);
        this.setUploadFeedback(
          uploadKey,
          "danger",
          error.message || `Import für '${fileType}' fehlgeschlagen.`
        );
        throw error;
      }
    },
    async migrateDeviceInfoTable() {
      this.isMigrating = true;
      this.migrationVariant = "info";
      this.migrationMessage = "Migration wird gestartet...";

      try {
        const migrate = httpsCallable(functions, "migrateDeviceInfoTable", {
          timeout: 540000,
        });
        const response = await migrate();
        const payload = response && response.data ? response.data : {};

        if (payload.started === false && payload.reason === "already-running") {
          const processedChunks = Number(payload.processedChunks || 0);
          const totalChunks = Number(payload.totalChunks || 0);
          const totalDocs = Number(payload.totalDocs || 0);
          const chunkSize = Number(payload.chunkSize || 0);
          const safeTotalChunks = this.resolveTotalChunks(
            totalChunks,
            totalDocs,
            chunkSize
          );
          const totalChunksLabel = safeTotalChunks > 0 ? safeTotalChunks : "?";
          this.migrationVariant = "warning";
          this.migrationMessage =
            "Migration läuft bereits. " +
            `Chunks: ${processedChunks} von ${totalChunksLabel}`;
          return;
        }

        const totalChunks = Number(payload.totalChunks || 0);
        const startInfo = totalChunks > 0 ? ` Chunks gesamt: ${totalChunks}.` : "";

        this.migrationVariant = "success";
        this.migrationMessage = payload.runId
          ? `Migration gestartet (Run-ID: ${payload.runId}).${startInfo}`
          : `Migration gestartet.${startInfo}`;
      } catch (error) {
        console.error("Fehler beim Aufruf von migrateDeviceInfoTable:", error);
        this.migrationVariant = "danger";
        this.migrationMessage =
          "Migration fehlgeschlagen. Details stehen in der Konsole.";
      } finally {
        this.isMigrating = false;
      }
    },
    async persistUploadData(data) {
      const uploadKey = data.uploadKey;
      const fileType = data.fileType;
      const fileData = data.fileData;

      if (!Array.isArray(fileData) || fileData.length === 0) {
        return;
      }

      const flattenedRows = fileData.flat();

      if (fileType === "Apple Finished Goods Report") {
        return await this.uploadJsonWithProcessingTracking(
          uploadKey,
          fileType,
          "uploads/apple-fgr.json",
          flattenedRows
        );
      }

      if (fileType === "Telekom iPhone Avisierungen") {
        return await this.uploadJsonWithProcessingTracking(
          uploadKey,
          fileType,
          "uploads/telekom-iphone-deliveries.json",
          flattenedRows
        );
      }

      if (fileType === "Apple Seriennummern") {
        return await this.uploadJsonWithProcessingTracking(
          uploadKey,
          fileType,
          "uploads/apple-sn.json",
          flattenedRows
        );
      }

      if (fileType === "Telekom IMEI") {
        return await this.uploadJsonWithProcessingTracking(
          uploadKey,
          fileType,
          "uploads/telekom-imei.json",
          flattenedRows
        );
      }

      if (fileType === "iPhone Telekom Ersatzlieferanten Artikelnummern") {
        return await this.uploadJsonWithProcessingTracking(
          uploadKey,
          fileType,
          "uploads/telekom-iphone-supplier-artnos.json",
          flattenedRows
        );
      }

      throw new Error("Unbekannter Upload-Typ.");
    },
  },
  mounted() {
    this.initPanelStates();
    this.refreshUploadMappings();
    this.loadUploadColumnConfig();
    this.startMigrationProgressListener();
  },
  beforeDestroy() {
    this.clearUploadStatusListener();

    if (this.migrationMetaUnsubscribe) {
      this.migrationMetaUnsubscribe();
      this.migrationMetaUnsubscribe = null;
    }
  },
};
</script>

<style scoped>
.upload-settings__message {
  display: block;
}

.upload-card {
  border: 1px solid #dbe4f0;
  border-radius: 4px;
  overflow: hidden;
}

.upload-card__header {
  background: #f7f9fc;
}

.upload-card__actions {
  justify-content: flex-end;
}

.upload-section-box {
  border: 1px solid #e3e8ef;
  border-radius: 4px;
  padding: 12px;
  background: #ffffff;
}

.upload-header-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.upload-header-chip {
  display: inline-flex;
  align-items: center;
  border: 1px solid #d0dbe8;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 12px;
  color: #2f3c4d;
  background: #f8fbff;
}

@media (max-width: 767px) {
  .upload-card__actions {
    width: 100%;
    margin-top: 8px;
    justify-content: flex-start;
  }
}
</style>
